import React, { FC, useEffect, useRef, useCallback, useMemo } from 'react';
import { AvailabilityStatus } from '../../redux/api/availability/availability.model';

import AvailableDefaultConnectedSVG from '../../assets/markers/available_default_connected.svg';
import AvailableHoverConnectedSVG from '../../assets/markers/available_hover_connected.svg';
import AvailableDefaultConnectedSelectedSVG from '../../assets/markers/available_default_connected_selected.svg';
import AvailableHoverConnectedSelectedSVG from '../../assets/markers/available_hover_connected_selected.svg';
import AvailableDefaultCachedSVG from '../../assets/markers/available_default_cached.svg';
import AvailableHoverCachedSVG from '../../assets/markers/available_hover_cached.svg';
import AvailableDefaultCachedSelectedSVG from '../../assets/markers/available_default_cached_selected.svg';
import AvailableHoverCachedSelectedSVG from '../../assets/markers/available_hover_cached_selected.svg';
import AvailableDefaultNoeldSVG from '../../assets/markers/available_default_noeld.svg';
import AvailableHoverNoeldSVG from '../../assets/markers/available_hover_noeld.svg';
import AvailableDefaultNoeldSelectedSVG from '../../assets/markers/available_default_noeld_selected.svg';
import AvailableHoverNoeldSelectedSVG from '../../assets/markers/available_hover_noeld_selected.svg';
import DispatchedDefaultConnectedSVG from '../../assets/markers/dispatched_default_connected.svg';
import DispatchedHoverConnectedSVG from '../../assets/markers/dispatched_hover_connected.svg';
import DispatchedDefaultConnectedSelectedSVG from '../../assets/markers/dispatched_default_connected_selected.svg';
import DispatchedHoverConnectedSelectedSVG from '../../assets/markers/dispatched_hover_connected_selected.svg';
import DispatchedDefaultCachedSVG from '../../assets/markers/dispatched_default_cached.svg';
import DispatchedHoverCachedSVG from '../../assets/markers/dispatched_hover_cached.svg';
import DispatchedDefaultCachedSelectedSVG from '../../assets/markers/dispatched_default_cached_selected.svg';
import DispatchedHoverCachedSelectedSVG from '../../assets/markers/dispatched_hover_cached_selected.svg';
import DispatchedDefaultNoeldSVG from '../../assets/markers/dispatched_default_noeld.svg';
import DispatchedHoverNoeldSVG from '../../assets/markers/dispatched_hover_noeld.svg';
import DispatchedDefaultNoeldSelectedSVG from '../../assets/markers/dispatched_default_noeld_selected.svg';
import DispatchedHoverNoeldSelectedSVG from '../../assets/markers/dispatched_hover_noeld_selected.svg';
import { ELDConnectionStatus } from '../../redux/api/equipment/equipment.model';

export enum LocationMarkerType {
  LOCATION_PIN = 'location-pin',
  STOP_SQUARE = 'stop-square',
  STOP_CIRCLE = 'stop-circle',
  LIVE_LOCATION = 'live-location'
}

export interface LocationMarker {
  position: google.maps.LatLng;
  type: LocationMarkerType;
  order?: number;
}

export interface TwMarkerProps extends google.maps.MarkerOptions {
  id?: string;
  timestamp?: string;
  lastUpdatedTimestamp?: string;
  type?: LocationMarkerType;
  isSelected?: boolean;
  isHovered?: boolean;
  status?: AvailabilityStatus;
  onMarkerClick?: () => void;
  onMarkerMouseEnter?: () => void;
  onMarkerMouseLeave?: () => void;
  title?: string;
  lastStop?: string;
  nextStop?: string;
  finalStop?: string;
  name?: string;
  eldConnectionStatus?: ELDConnectionStatus; // Fixed: Changed type to ELDConnectionStatus
}

export interface LiveLocationMarker extends LocationMarker {
  timestamp?: string;
  lastUpdatedTimestamp?: string;
}

const TwMarker: FC<TwMarkerProps> = (options) => {
  const markerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const getMarkerSVG = useCallback(
    (
      status: AvailabilityStatus,
      eldConnectionStatus: ELDConnectionStatus,
      isSelected: boolean,
      isHovered: boolean
    ): string => {
      if (status === AvailabilityStatus.available) {
        if (eldConnectionStatus === ELDConnectionStatus.CONNECTED) {
          if (isSelected && isHovered) return AvailableHoverConnectedSelectedSVG;
          if (isSelected) return AvailableDefaultConnectedSelectedSVG;
          if (isHovered) return AvailableHoverConnectedSVG;
          return AvailableDefaultConnectedSVG;
        } else if (eldConnectionStatus === ELDConnectionStatus.CACHED) {
          if (isSelected && isHovered) return AvailableHoverCachedSelectedSVG;
          if (isSelected) return AvailableDefaultCachedSelectedSVG;
          if (isHovered) return AvailableHoverCachedSVG;
          return AvailableDefaultCachedSVG;
        } else {
          // noeld
          if (isSelected && isHovered) return AvailableHoverNoeldSelectedSVG;
          if (isSelected) return AvailableDefaultNoeldSelectedSVG;
          if (isHovered) return AvailableHoverNoeldSVG;
          return AvailableDefaultNoeldSVG;
        }
      } else {
        // dispatched
        if (eldConnectionStatus === ELDConnectionStatus.CONNECTED) {
          if (isSelected && isHovered) return DispatchedHoverConnectedSelectedSVG;
          if (isSelected) return DispatchedDefaultConnectedSelectedSVG;
          if (isHovered) return DispatchedHoverConnectedSVG;
          return DispatchedDefaultConnectedSVG;
        } else if (eldConnectionStatus === ELDConnectionStatus.CACHED) {
          if (isSelected && isHovered) return DispatchedHoverCachedSelectedSVG;
          if (isSelected) return DispatchedDefaultCachedSelectedSVG;
          if (isHovered) return DispatchedHoverCachedSVG;
          return DispatchedDefaultCachedSVG;
        } else {
          // noeld
          if (isSelected && isHovered) return DispatchedHoverNoeldSelectedSVG;
          if (isSelected) return DispatchedDefaultNoeldSelectedSVG;
          if (isHovered) return DispatchedHoverNoeldSVG;
          return DispatchedDefaultNoeldSVG;
        }
      }
    },
    []
  );

  const createDynamicIcon = useCallback(
    (
      status: AvailabilityStatus,
      eldConnectionStatus: ELDConnectionStatus,
      isSelected: boolean,
      isHovered: boolean
    ): google.maps.Icon => {
      const svgUrl = getMarkerSVG(status, eldConnectionStatus, isSelected, isHovered);
      const size = isSelected ? 36 : 32; // Larger size for selected markers
      return {
        url: svgUrl,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2) // Center the icon
      };
    },
    [getMarkerSVG]
  );

  const createInfoWindowStyles = useCallback(
    (): string => `
    <style>
      .custom-info-window {
        color: white;
        min-width: 200px;
        padding: 12px;
        padding-top: 0px;
        border-radius: 4px;
        background-color: #1e293b !important;
        font-family: Poppins, sans-serif;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        overflow: hidden;
      }
      .info-text { font-size: 12px; line-height: 20px; text-align: left; margin: 0; }
      .info-title { 
        font-weight: 500; 
        margin-top: 0;
        margin-bottom: 8px;
        font-size: 14px;
      }
      .info-content { font-weight: 400; }
      .info-time { color: #9CA3AF; }
      .gm-style-iw, .gm-style-iw-c, .gm-style-iw-d { padding: 0 !important; margin: 0 !important; border-radius: 4px !important; overflow: hidden !important; background: #1e293b !important; }
      .gm-style-iw-t::after, .gm-ui-hover-effect { display: none !important; }
    </style>
  `,
    []
  );

  const createInfoWindowContent = useCallback((): string | null => {
    if (options.type !== LocationMarkerType.LIVE_LOCATION) {
      const styles = createInfoWindowStyles();
      const title =
        options.name ||
        options.title ||
        (typeof options.label === 'string' ? options.label : options.label?.text || '');
      const timestamp = options.timestamp
        ? `<p class="info-text info-content info-time">Last updated: ${options.timestamp}</p>`
        : `<p class="info-text info-content info-time">Last updated: -</p>`;
      const lastStop = `<p class="info-text info-content">Last stop: ${
        options.lastStop || '-'
      }</p>`;
      const nextStop = `<p class="info-text info-content">Next stop: ${
        options.nextStop || '-'
      }</p>`;
      const finalStop = `<p class="info-text info-content">Final stop: ${
        options.finalStop || '-'
      }</p>`;

      return `
          ${styles}
          <div class="custom-info-window">
              <p class="info-text info-title">${title}</p>
              ${timestamp}
              ${lastStop}
              ${nextStop}
              ${finalStop}
          </div>
      `;
    }

    // for live location marker in overlays
    const styles = createInfoWindowStyles();
    const timestamp = options.timestamp
      ? `<p class="info-text info-content info-time">Last updated: ${options.timestamp}</p>`
      : `<p class="info-text info-content info-time">Last updated: -</p>`;

    const lastUpdatedTimestamp = options.lastUpdatedTimestamp
      ? `<p class="info-text info-content info-time">Last attempt to update location: ${options.lastUpdatedTimestamp}</p>`
      : ``;

    return `
      ${styles}
      <div class="custom-info-window">
          ${timestamp}
          ${lastUpdatedTimestamp}
      </div>`;
  }, [
    options.lastStop,
    options.name,
    options.title,
    options.label,
    options.timestamp,
    options.lastUpdatedTimestamp,
    options.nextStop,
    options.finalStop,
    createInfoWindowStyles
  ]);

  const initializeMarker = useCallback((): void => {
    if (!markerRef.current && window.google) {
      markerRef.current = new window.google.maps.Marker();
    }
  }, []);

  const initializeInfoWindow = useCallback((): void => {
    if (!infoWindowRef.current && window.google) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }
  }, []);

  const markerOptions = useMemo(() => {
    const updatedOptions = { ...options };
    if (options.status !== undefined && options.eldConnectionStatus !== undefined) {
      updatedOptions.icon = createDynamicIcon(
        options.status,
        options.eldConnectionStatus,
        options.isSelected || false,
        options.isHovered || false
      );
    }
    return updatedOptions;
  }, [
    options.status,
    options.eldConnectionStatus,
    options.isSelected,
    options.isHovered,
    options.position,
    options.map,
    createDynamicIcon
  ]);

  useEffect(() => {
    initializeMarker();
    initializeInfoWindow();

    return (): void => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, [initializeMarker, initializeInfoWindow]);

  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setOptions(markerOptions);
    }
  }, [markerOptions]);

  useEffect(() => {
    if (markerRef.current && infoWindowRef.current) {
      const listeners = [
        markerRef.current.addListener('mouseover', () => {
          const content = createInfoWindowContent();
          if (content) {
            infoWindowRef.current!.setContent(content);
            infoWindowRef.current!.open(options.map, markerRef.current);
          }
          options.onMarkerMouseEnter?.();
        }),
        markerRef.current.addListener('mouseout', () => {
          infoWindowRef.current!.close();
          options.onMarkerMouseLeave?.();
        }),
        markerRef.current.addListener('click', () => {
          options.onMarkerClick?.();
        })
      ];

      return (): void => {
        listeners.forEach((listener) => window.google?.maps.event.removeListener(listener));
      };
    }
  }, [options, createInfoWindowContent]);

  return null;
};

export default TwMarker;
